(function () {
  'use strict';

  const CACHE_CLEANUP_THRESHOLD = 20;
  const CACHE_MAX_ENTRIES = 15;

  function normalizeId(value, gidPrefix) {
    const raw = (value == null ? '' : String(value)).trim();
    if (!raw) return '';
    if (gidPrefix && raw.indexOf(gidPrefix) !== -1) {
      const parts = raw.split('/');
      return parts[parts.length - 1] || raw;
    }
    return raw;
  }

  function normalizeLocationId(value) {
    return normalizeId(value, 'gid://shopify/Location/');
  }

  function normalizeProductId(value) {
    return normalizeId(value, 'gid://shopify/Product/');
  }

  function normalizeEntityId(value) {
    return normalizeId(value, 'gid://');
  }

  function escapeForSelector(str) {
    if (typeof str !== 'string') str = str == null ? '' : String(str);
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(str);
    }
    // CSS.escape polyfill per https://drafts.csswg.org/cssom/#serialize-an-identifier
    var length = str.length;
    var index = -1;
    var codeUnit;
    var result = '';
    var firstCodeUnit = length > 0 ? str.charCodeAt(0) : 0;
    if (length === 1 && firstCodeUnit === 0x002D) return '\\' + str;
    while (++index < length) {
      codeUnit = str.charCodeAt(index);
      if (codeUnit === 0x0000) {
        result += '\uFFFD';
        continue;
      }
      if (
        (codeUnit >= 0x0001 && codeUnit <= 0x001F) || codeUnit === 0x007F ||
        (index === 0 && codeUnit >= 0x0030 && codeUnit <= 0x0039) ||
        (index === 1 && codeUnit >= 0x0030 && codeUnit <= 0x0039 && firstCodeUnit === 0x002D)
      ) {
        result += '\\' + codeUnit.toString(16) + ' ';
        continue;
      }
      if (
        codeUnit >= 0x0080 ||
        codeUnit === 0x002D ||
        codeUnit === 0x005F ||
        (codeUnit >= 0x0030 && codeUnit <= 0x0039) ||
        (codeUnit >= 0x0041 && codeUnit <= 0x005A) ||
        (codeUnit >= 0x0061 && codeUnit <= 0x007A)
      ) {
        result += str.charAt(index);
        continue;
      }
      result += '\\' + str.charAt(index);
    }
    return result;
  }

  function isPayItForwardProduct(product) {
    if (!product || !product.collections || !product.collections.length) return false;
    for (let i = 0; i < product.collections.length; i++) {
      const collection = product.collections[i];
      const handle = collection && collection.handle;
      if (handle === 'pay-it-forward-collection' || handle === 'pay-it-forward') {
        return true;
      }
    }
    return false;
  }

  function getToGoInventoryForProduct(product) {
    if (!product || !product.variants) {
      return null;
    }
    for (const variantIdKey in product.variants) {
      if (!Object.prototype.hasOwnProperty.call(product.variants, variantIdKey)) continue;
      const variant = product.variants[variantIdKey];
      if (variant && variant.inventory) {
        return {
          available: Math.max(0, Number(variant.inventory.available) || 0),
          committed: 0,
          incoming: Number(variant.inventory.incoming) || 0
        };
      }
    }
    return null;
  }

  function findComponentInventory(productsMap, productId) {
    if (!productsMap) return null;
    const normalized = normalizeProductId(productId);
    const product = productsMap[normalized] || productsMap[productId];
    if (!product || !product.variants) return null;
    return getToGoInventoryForProduct(product);
  }

  function getBundleInventory(productsMap, productId) {
    if (!productsMap) {
      return {
        available: 0,
        committed: 0,
        incoming: 0
      };
    }

    const normalized = normalizeProductId(productId);
    const product = productsMap[normalized] || productsMap[productId];
    if (!product) {
      return {
        available: 0,
        committed: 0,
        incoming: 0
      };
    }

    if (!product.bundleComponents || !Array.isArray(product.bundleComponents) || product.bundleComponents.length === 0) {
      return {
        available: 0,
        committed: 0,
        incoming: 0
      };
    }

    const bundleComponents = product.bundleComponents;
    let minAvailable = Infinity;
    let totalIncoming = 0;

    for (let i = 0; i < bundleComponents.length; i++) {
      const component = bundleComponents[i];
      const componentProductId = component && component.product_id;
      const componentQuantity = component && component.quantity ? component.quantity : 1;

      if (!componentProductId) {
        minAvailable = 0;
        continue;
      }

      const componentInventory = findComponentInventory(productsMap, componentProductId);
      if (componentInventory) {
        const availableForBundle = Math.floor((Number(componentInventory.available) || 0) / componentQuantity);
        minAvailable = Math.min(minAvailable, availableForBundle);
        totalIncoming += Math.floor((Number(componentInventory.incoming) || 0) / componentQuantity);
      } else {
        minAvailable = 0;
      }
    }

    if (minAvailable === Infinity) {
      minAvailable = 0;
    }

    return {
      available: minAvailable,
      committed: 0,
      incoming: totalIncoming
    };
  }

  const API_URL = (typeof window !== 'undefined' && window.EveryTable?.api?.baseUrl) || 'https://admin-app.everytable-sh.com/api';
  const CACHE_TTL_MS = 15 * 60 * 1000;
  const _inFlightInventory = {};

  function fetchInventoryForLocation(locationId) {
    const normalized = normalizeLocationId(locationId);
    if (!normalized) return Promise.resolve(null);

    const cacheKey = 'inventory_data_' + normalized;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed.timestamp && (Date.now() - parsed.timestamp) < CACHE_TTL_MS) {
          return Promise.resolve(parsed.data);
        }
      }
    } catch (e) {}

    if (_inFlightInventory[normalized]) {
      return _inFlightInventory[normalized];
    }

    const promise = fetch(API_URL + '/location/' + encodeURIComponent(normalized) + '/inventory')
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        try {
          const cacheData = { data: data, timestamp: Date.now(), locationId: normalized };
          sessionStorage.setItem(cacheKey, JSON.stringify(cacheData));
          cleanupStaleSessionStorage();
        } catch (e) {}
        delete _inFlightInventory[normalized];
        return data;
      })
      .catch(function (err) {
        delete _inFlightInventory[normalized];
        throw err;
      });

    _inFlightInventory[normalized] = promise;
    return promise;
  }

  function cleanupStaleSessionStorage() {
    try {
      const keys = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.indexOf('inventory_data_') === 0) {
          keys.push(key);
        }
      }
      if (keys.length <= CACHE_CLEANUP_THRESHOLD) return;
      const entries = [];
      for (let i = 0; i < keys.length; i++) {
        try {
          const raw = sessionStorage.getItem(keys[i]);
          const parsed = raw ? JSON.parse(raw) : null;
          const ts = parsed && parsed.timestamp ? parsed.timestamp : 0;
          entries.push({ key: keys[i], ts: ts });
        } catch (e) {}
      }
      entries.sort(function (a, b) { return a.ts - b.ts; });
      const toRemove = Math.max(0, entries.length - CACHE_MAX_ENTRIES);
      for (let i = 0; i < toRemove; i++) {
        sessionStorage.removeItem(entries[i].key);
      }
    } catch (e) {}
  }

  window.EtInventoryDomain = {
    normalizeLocationId: normalizeLocationId,
    normalizeProductId: normalizeProductId,
    normalizeEntityId: normalizeEntityId,
    isPayItForwardProduct: isPayItForwardProduct,
    getToGoInventoryForProduct: getToGoInventoryForProduct,
    findComponentInventory: findComponentInventory,
    getBundleInventory: getBundleInventory,
    cleanupStaleSessionStorage: cleanupStaleSessionStorage,
    escapeForSelector: escapeForSelector,
    fetchInventoryForLocation: fetchInventoryForLocation
  };

  if (typeof window !== 'undefined' && typeof document !== 'undefined') {
    if (document.readyState === 'loading') {
      window.addEventListener('DOMContentLoaded', cleanupStaleSessionStorage);
    } else {
      cleanupStaleSessionStorage();
    }
  }
})();
